import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { 
  Cloud, 
  LayoutDashboard, 
  Receipt, 
  Users, 
  Key, 
  Tag, 
  ShoppingCart,
  TrendingUp,
  DollarSign,
  Activity,
  ArrowUpRight,
  ArrowDownRight,
  CreditCard,
  LogOut,
  Loader2,
  Banknote,
  UserCog,
  Code2,
  Building2,
  Settings
} from "lucide-react";
import axios from "axios";
import { useAdminAuth, getAdminAuthHeaders } from "@/hooks/useAdminAuth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Sidebar = ({ onLogout }) => {
  const location = useLocation();
  
  const navItems = [
    { path: "/admin", icon: <LayoutDashboard className="w-5 h-5" />, label: "Dashboard" },
    { path: "/admin/revenue", icon: <Banknote className="w-5 h-5" />, label: "Revenue" },
    { path: "/admin/users", icon: <UserCog className="w-5 h-5" />, label: "Users" },
    { path: "/admin/transactions", icon: <Receipt className="w-5 h-5" />, label: "Transactions" },
    { path: "/admin/billing", icon: <CreditCard className="w-5 h-5" />, label: "Weekly Billing" },
    { path: "/admin/customers", icon: <Users className="w-5 h-5" />, label: "Customers" },
    { path: "/admin/licenses", icon: <Key className="w-5 h-5" />, label: "Licenses" },
    { path: "/admin/coupons", icon: <Tag className="w-5 h-5" />, label: "Coupons" },
    { path: "/admin/sales", icon: <ShoppingCart className="w-5 h-5" />, label: "Sales" },
    { path: "/admin/embeds", icon: <Code2 className="w-5 h-5" />, label: "Embed Widgets" },
    { path: "/admin/merchant", icon: <Building2 className="w-5 h-5" />, label: "Merchant Fees" },
    { path: "/admin/settings", icon: <Settings className="w-5 h-5" />, label: "Platform Settings" },
  ];

  return (
    <aside className="w-64 bg-white border-r border-slate-200 min-h-screen fixed left-0 top-0">
      <div className="p-6 border-b border-slate-200">
        <Link to="/" className="flex items-center gap-2">
          <img src="/logo.png" alt="CloudSync Pro" className="h-14 object-contain" />
        </Link>
      </div>
      <nav className="p-4">
        <ul className="space-y-1">
          {navItems.map((item) => (
            <li key={item.path}>
              <Link 
                to={item.path}
                className={`sidebar-link ${location.pathname === item.path ? 'active' : ''}`}
              >
                {item.icon}
                <span>{item.label}</span>
              </Link>
            </li>
          ))}
        </ul>
      </nav>
      <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-200 space-y-2">
        <button
          onClick={onLogout}
          className="flex items-center gap-2 text-sm text-red-500 hover:text-red-600 w-full"
          data-testid="admin-logout-btn"
        >
          <LogOut className="w-4 h-4" />
          Logout
        </button>
        <Link to="/" className="text-sm text-slate-500 hover:text-slate-700 block">
          ← Back to Website
        </Link>
      </div>
    </aside>
  );
};

export { Sidebar };

export default function AdminDashboard() {
  const { isLoading: authLoading, isAuthenticated, logout } = useAdminAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isAuthenticated) {
      fetchStats();
    }
  }, [isAuthenticated]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/dashboard/stats`, {
        headers: getAdminAuthHeaders()
      });
      setStats(response.data);
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-bronze" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Sidebar onLogout={logout} />
      
      <main className="ml-64 p-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-midnight font-outfit" data-testid="dashboard-title">
            Dashboard
          </h1>
          <p className="text-slate-600">Overview of your CloudSync Pro business</p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="spinner" />
          </div>
        ) : (
          <>
            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <Card className="stat-card" data-testid="stat-plugin-sales">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 rounded-lg bg-bronze/10 flex items-center justify-center">
                      <ShoppingCart className="w-6 h-6 text-bronze" />
                    </div>
                    <Badge className="bg-emerald-100 text-emerald-700">
                      <ArrowUpRight className="w-3 h-3 mr-1" />
                      12%
                    </Badge>
                  </div>
                  <p className="text-sm text-slate-600 mb-1">Plugin Sales</p>
                  <p className="text-3xl font-bold text-midnight font-outfit stat-value">
                    {stats?.plugin_sales?.total || 0}
                  </p>
                </CardContent>
              </Card>

              <Card className="stat-card" data-testid="stat-revenue">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 rounded-lg bg-emerald-100 flex items-center justify-center">
                      <DollarSign className="w-6 h-6 text-emerald-600" />
                    </div>
                    <Badge className="bg-emerald-100 text-emerald-700">
                      <ArrowUpRight className="w-3 h-3 mr-1" />
                      8%
                    </Badge>
                  </div>
                  <p className="text-sm text-slate-600 mb-1">Plugin Revenue</p>
                  <p className="text-3xl font-bold text-midnight font-outfit stat-value">
                    {formatCurrency(stats?.plugin_sales?.revenue || 0)}
                  </p>
                </CardContent>
              </Card>

              <Card className="stat-card" data-testid="stat-bookings">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
                      <Activity className="w-6 h-6 text-blue-600" />
                    </div>
                    <Badge className="bg-blue-100 text-blue-700">
                      <ArrowUpRight className="w-3 h-3 mr-1" />
                      24%
                    </Badge>
                  </div>
                  <p className="text-sm text-slate-600 mb-1">Total Bookings</p>
                  <p className="text-3xl font-bold text-midnight font-outfit stat-value">
                    {stats?.bookings?.total || 0}
                  </p>
                </CardContent>
              </Card>

              <Card className="stat-card" data-testid="stat-fees">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 rounded-lg bg-purple-100 flex items-center justify-center">
                      <TrendingUp className="w-6 h-6 text-purple-600" />
                    </div>
                    <Badge className="bg-purple-100 text-purple-700">
                      2% Fee
                    </Badge>
                  </div>
                  <p className="text-sm text-slate-600 mb-1">Transaction Fees</p>
                  <p className="text-3xl font-bold text-midnight font-outfit stat-value">
                    {formatCurrency(stats?.bookings?.fees_earned || 0)}
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Recent Activity */}
            <div className="grid lg:grid-cols-2 gap-6">
              <Card data-testid="recent-sales-card">
                <CardHeader>
                  <CardTitle className="font-outfit flex items-center justify-between">
                    Recent Plugin Sales
                    <Link to="/admin/sales" className="text-sm text-bronze font-normal hover:underline">
                      View all
                    </Link>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {stats?.recent_sales?.length > 0 ? (
                    <div className="space-y-4">
                      {stats.recent_sales.map((sale, index) => (
                        <div key={index} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                          <div>
                            <p className="font-medium text-midnight">{sale.customer_email}</p>
                            <p className="text-sm text-slate-500">{formatDate(sale.created_at)}</p>
                          </div>
                          <div className="text-right">
                            <p className="font-semibold text-emerald-600">{formatCurrency(sale.amount)}</p>
                            <Badge className={sale.payment_status === 'paid' ? 'status-success' : 'status-pending'}>
                              {sale.payment_status}
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-slate-500 text-center py-8">No sales yet</p>
                  )}
                </CardContent>
              </Card>

              <Card data-testid="recent-transactions-card">
                <CardHeader>
                  <CardTitle className="font-outfit flex items-center justify-between">
                    Recent Booking Transactions
                    <Link to="/admin/transactions" className="text-sm text-bronze font-normal hover:underline">
                      View all
                    </Link>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {stats?.recent_transactions?.length > 0 ? (
                    <div className="space-y-4">
                      {stats.recent_transactions.map((tx, index) => (
                        <div key={index} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                          <div>
                            <p className="font-medium text-midnight">{tx.guest_name}</p>
                            <p className="text-sm text-slate-500">{tx.room_type}</p>
                          </div>
                          <div className="text-right">
                            <p className="font-semibold">{formatCurrency(tx.booking_amount)}</p>
                            <p className="text-xs text-slate-500">Fee: {formatCurrency(tx.fee_amount)}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-slate-500 text-center py-8">No transactions yet</p>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Active Licenses */}
            <Card className="mt-6" data-testid="active-licenses-card">
              <CardHeader>
                <CardTitle className="font-outfit">License Overview</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-8">
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 rounded-full bg-emerald-500" />
                    <span className="text-slate-600">Active Licenses:</span>
                    <span className="font-bold text-midnight">{stats?.licenses?.active || 0}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 rounded-full bg-blue-500" />
                    <span className="text-slate-600">Booking Volume:</span>
                    <span className="font-bold text-midnight">{formatCurrency(stats?.bookings?.volume || 0)}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </main>
    </div>
  );
}
